"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useId, useState } from "react";

type ReadMoreBlockProps = {
  collapsedContent: React.ReactNode;
  expandedContent: React.ReactNode;
  readFullHref?: string;
  readFullLabel?: string;
  className?: string;
  /** Class for the expand/collapse + optional read-full row (spacing, gaps). */
  linksClassName?: string;
};

export const readMoreTextLinkClass =
  "text-sm font-semibold text-[var(--primary)] underline underline-offset-[5px] decoration-[color-mix(in_srgb,var(--primary)_28%,transparent)] hover:decoration-[var(--primary)] transition-colors";

export function ReadMoreBlock({
  collapsedContent,
  expandedContent,
  readFullHref,
  readFullLabel,
  className = "",
  linksClassName = "mt-4 flex flex-wrap items-baseline gap-x-7 gap-y-2",
}: ReadMoreBlockProps) {
  const t = useTranslations("readMore");
  const fullLabel = readFullLabel ?? t("readFull");
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className={className}>
      <div>{collapsedContent}</div>
      {open ? (
        <div id={panelId} className="mt-4 space-y-4">
          {expandedContent}
        </div>
      ) : (
        <div id={panelId} className="hidden" aria-hidden />
      )}
      <div className={linksClassName}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`${readMoreTextLinkClass} bg-transparent border-0 p-0 cursor-pointer text-left`}
          style={{ fontFamily: "var(--font-body)" }}
          aria-expanded={open}
          aria-controls={panelId}
        >
          {open ? t("collapse") : t("expand")}
        </button>
        {readFullHref ? (
          <Link
            href={readFullHref}
            className={readMoreTextLinkClass}
            style={{ fontFamily: "var(--font-body)" }}
          >
            {fullLabel}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
